import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Globe } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../../lib/auth";
import useSEO from "../../lib/seo";
import { toast } from "sonner";

export default function AdminLogin() {
  useSEO({ title: "Admin Login", noindex: true, path: "/admin/login" });
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (e2) {
      const msg = formatApiErrorDetail(e2?.response?.data?.detail) || "Login failed";
      setErr(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-neutral-100 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-5">
            <span className="relative inline-flex h-12 w-12 items-center justify-center">
              <span className="absolute inset-0 bg-[#dc2626] -skew-x-12" />
              <span className="relative font-display font-black text-white text-2xl">
                D
              </span>
            </span>
            <span className="relative inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#1d4ed8] text-white">
              <Globe className="h-6 w-6" strokeWidth={2} />
            </span>
          </div>
          <h1 className="font-display font-bold text-3xl tracking-tight text-white">
            Admin Console
          </h1>
          <p className="text-neutral-400 text-sm mt-2">
            Sign in to manage leads, contacts and visitors.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="admin-login-form"
          className="border border-white/10 rounded-xl bg-[#0f0f0f] p-8 space-y-5"
        >
          <div>
            <label className="block text-xs font-bold tracking-[0.2em] uppercase text-neutral-400 mb-2 font-mono">
              Email
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@dvpdigiworld.com"
              data-testid="admin-login-email"
              className="w-full bg-[#1a1a1a] border border-white/10 text-white placeholder-neutral-500 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#dc2626]"
            />
          </div>
          <div>
            <label className="block text-xs font-bold tracking-[0.2em] uppercase text-neutral-400 mb-2 font-mono">
              Password
            </label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password"
              className="w-full bg-[#1a1a1a] border border-white/10 text-white placeholder-neutral-500 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#dc2626]"
            />
          </div>
          {err && (
            <div className="text-xs text-[#dc2626] bg-[#dc2626]/10 border border-[#dc2626]/20 rounded px-3 py-2">
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#dc2626] hover:bg-[#ef4444] disabled:opacity-70 text-white px-5 py-3 font-semibold"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Sign in
          </button>
        </form>

        <p className="text-center text-[11px] text-neutral-600 font-mono mt-6 uppercase tracking-[0.2em]">
          Authorized personnel only
        </p>
      </div>
    </div>
  );
}
